import { useState } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Plus, Minus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { FAQ_INTRO } from "../lib/faqData";

const API = process.env.REACT_APP_BACKEND_URL;

export const FAQAccordion = ({ faqs, idPrefix = "faq" }) => (
  <AccordionPrimitive.Root type="multiple" className="w-full" data-testid={`${idPrefix}-accordion`}>
    {faqs.map((f, i) => (
      <AccordionPrimitive.Item key={i} value={`${idPrefix}-${i}`} className="border-b border-ink/10" data-testid={`${idPrefix}-item-${i}`}>
        <AccordionPrimitive.Header className="flex">
          <AccordionPrimitive.Trigger
            className="group flex flex-1 items-center justify-between gap-6 py-5 text-left font-display font-semibold text-base lg:text-lg text-ink hover:text-skope transition-colors"
            data-testid={`${idPrefix}-question-${i}`}
          >
            <span>{f.q}</span>
            <span className="shrink-0 w-8 h-8 flex items-center justify-center border border-ink/15 text-ink/60 group-hover:border-skope group-hover:text-skope transition-colors">
              <Plus size={15} className="group-data-[state=open]:hidden" />
              <Minus size={15} className="hidden group-data-[state=open]:block" />
            </span>
          </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
        <AccordionPrimitive.Content
          className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
          data-testid={`${idPrefix}-answer-${i}`}
        >
          <p className="pb-6 pr-14 text-ink/60 leading-relaxed text-base">{f.a}</p>
        </AccordionPrimitive.Content>
      </AccordionPrimitive.Item>
    ))}
  </AccordionPrimitive.Root>
);

export const StillHaveQuestions = ({ source }) => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/faq-question`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source }),
      });
      if (!res.ok) throw new Error();
      toast.success("Thanks — we'll get in touch ASAP.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please email contact@skopekitchens.com");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mt-14 border border-ink/10 p-6 lg:p-8" data-testid="still-have-questions">
      <h3 className="font-display font-semibold text-xl text-ink tracking-tight">Still have questions?</h3>
      <p className="mt-2 text-ink/55 text-sm leading-relaxed">
        Can't find the answer you're looking for? Enter your email address, we'll get in touch with you ASAP.
      </p>
      <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourbrand.com"
          className="flex-1 border border-ink/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-ink/30 focus:outline-none focus:border-skope transition-colors"
          data-testid="faq-email-input"
        />
        <button
          type="submit"
          disabled={sending}
          className="flex items-center justify-center gap-2 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-7 py-3.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-50"
          data-testid="faq-email-submit"
        >
          {sending ? "Sending…" : "Get In Touch"} <ArrowRight size={14} />
        </button>
      </form>
      <p className="mt-3 text-xs text-ink/40">
        Or write to us directly at{" "}
        <a href="mailto:contact@skopekitchens.com" className="text-skope hover:underline" data-testid="faq-email-direct">
          contact@skopekitchens.com
        </a>
      </p>
    </div>
  );
};

const FAQSection = ({ faqs, idPrefix = "faq", intro = FAQ_INTRO, source = "", dark = false }) => (
  <section className={dark ? "bg-ink text-white" : "bg-[#f7f7f5]"} data-testid={`${idPrefix}-section`}>
    <div className="max-w-[900px] mx-auto px-6 py-20 lg:py-28">
      <Reveal>
        <div className={`flex items-center gap-3 font-mono text-[11px] tracking-[0.35em] uppercase ${dark ? "text-skope" : "text-skope"}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-skope" />
          FAQ
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className={`mt-5 font-display font-bold text-3xl lg:text-5xl tracking-tight ${dark ? "text-white" : "text-ink"}`}>
          Frequently asked questions
        </h2>
      </Reveal>
      {intro && (
        <Reveal delay={0.14}>
          <p className={`mt-4 leading-relaxed max-w-2xl ${dark ? "text-white/55" : "text-ink/55"}`}>{intro}</p>
        </Reveal>
      )}
      <Reveal delay={0.2}>
        <div className="mt-10">
          <FAQAccordion faqs={faqs} idPrefix={idPrefix} />
        </div>
      </Reveal>
      <StillHaveQuestions source={source} />
    </div>
  </section>
);

export default FAQSection;
